import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CustomerDetails {
  name: string;
  email: string;
  address_line_1: string;
  address_line_2: string;
  town: string;
  complete_postcode: string;
  postcode_id: number | undefined; // Updated to allow undefined
  phone_no_home: string;
  phone_no_mobile: string;
  receive_marketing_emails: boolean;
}

export interface DeliveryDetails {
  postcode_id: number;
  venue: string;
  address_line_1: string;
  address_line_2: string;
  complete_postcode: string;
  town: string;
}

interface AccessibilityOption {
  question_id: number;
  option_id: number | null;
  free_text: string | null;
  charges: number;
}

interface BookingStatus {
  id: number;
  name: string;
  slug: string;
  type: string;
}

interface BookingState {
  customerDetails: CustomerDetails;
  deliveryDetails: DeliveryDetails;
  postcodes: {
    id: number;
    postcode: string;
    town: string;
    region: string;
    country: string;
    country_string: string;
    charges: string;
  }[];
  bookingStatuses: { 
    id: number; 
    name: string; 
    slug: string; 
    type: string; 
  }[];
  selectedPostcode: {
    postcode: string;
    town: string;
    region: string;
    charges: number;
  } | null;
  accessibility_options: AccessibilityOption[];
  booking_notes: string;
  booking_status: string;
  operator_notes: string;
  referral_source: string; 
  rental_agreement_signature: string | null;
  delivery_agreement_signature: string | null;
  collection_agreement_signature: string | null;
}

const initialState: BookingState = {  
  customerDetails: {
    name: '',
    email: '',
    address_line_1: '',
    address_line_2: '',
    town: '',
    complete_postcode: '',
    postcode_id: 0,
    phone_no_home: '',
    phone_no_mobile: '',
    receive_marketing_emails: false,
  },
  deliveryDetails: {
    postcode_id: 0,
    venue: '',
    address_line_1: '',
    address_line_2: '',
    complete_postcode: '',
    town: ''
  },
  postcodes: [],
  selectedPostcode: null,
  bookingStatuses: [],
  accessibility_options: [],
  booking_notes: '',
  booking_status: '',
  operator_notes: '',
  referral_source: '', 
  rental_agreement_signature: null,
  delivery_agreement_signature: null,
  collection_agreement_signature: null,
};

export const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    updateCustomerDetails: (state, action: PayloadAction<Partial<CustomerDetails>>) => {
      state.customerDetails = { ...state.customerDetails, ...action.payload };
    },
    updateDeliveryDetails: (state, action: PayloadAction<Partial<DeliveryDetails>>) => {
      state.deliveryDetails = { ...state.deliveryDetails, ...action.payload };
    },
    setPostcodeDetails: (state, action: PayloadAction<BookingState['postcodes']>) => {
      state.postcodes = action.payload;
    },
    setSelectedPostcode: (state, action: PayloadAction<BookingState['selectedPostcode']>) => {
      state.selectedPostcode = action.payload;
    },
    setBookingStatuses: (state, action: PayloadAction<BookingStatus[]>) => {
      state.bookingStatuses = action.payload;
    },
    resetBookingForm: () => {
      return initialState;
    },
    setAccessibilityOptions: (state, action: PayloadAction<AccessibilityOption[]>) => {
      state.accessibility_options = action.payload;
    },    
    setBookingNotes: (state, action: PayloadAction<string>) => {
      state.booking_notes = action.payload;
    },
    setRentalAgreementSignature: (state, action: PayloadAction<string | null>) => {
      state.rental_agreement_signature = action.payload;
    },
    setBookingStatus: (state, action: PayloadAction<string>) => {
      state.booking_status = action.payload;
    },
    setOperatorNotes: (state, action: PayloadAction<string>) => {
      state.operator_notes = action.payload;
    },
    setReferralSource: (state, action: PayloadAction<string>) => {
      state.referral_source = action.payload;
    },
    setInitialBookingData : (state, action) => {
      const { 
        customer, 
        delivery, 
        accessibility_options, 
        booking_notes,
        booking_status, 
        operator_notes, 
        rental_agreement_signature, 
        selectedPostcode, 
        referral_source
      } = action.payload;
      state.customerDetails = customer;
      state.deliveryDetails = delivery;
      state.accessibility_options = accessibility_options || [];
      state.booking_notes = booking_notes || '';
      state.booking_status = booking_status || '';
      state.operator_notes = operator_notes || '';
      state.rental_agreement_signature = rental_agreement_signature || null;
      state.selectedPostcode = selectedPostcode || null;
      state.referral_source = referral_source || '';
    }
  }
});

export const {
  updateCustomerDetails,
  updateDeliveryDetails,
  setPostcodeDetails,
  setBookingStatuses,
  resetBookingForm,
  setReferralSource,
  setSelectedPostcode,
  setAccessibilityOptions,
  setBookingNotes, 
  setRentalAgreementSignature,
  setBookingStatus,
  setOperatorNotes,
  setInitialBookingData,
} = bookingSlice.actions;

// Export selectors
export const selectPostcodes = (state) => state.booking.postcodes;
export const selectSelectedPostcode = (state) => state.booking.selectedPostcode;
export const selectBookingStatuses = (state) => state.booking.bookingStatuses;
export const selectBookingFormData = (state) => state.booking.customerDetails;
export const selectDeliveryDetails = (state) => state.booking.deliveryDetails;
export const selectAccessibilityOptions = (state) => state.booking.accessibility_options;
export const selectBookingNotes = (state) => state.booking.booking_notes;
export const selectReferralSource = (state) => state.booking.referral_source;
export const selectRentalAgreementSignature = (state) => state.booking.rental_agreement_signature;
export const selectBookingStatus = (state) => state.booking.booking_status;
export const selectOperatorNotes = (state) => state.booking.operator_notes;
export const selectBooking = (state) => state.booking;

export default bookingSlice.reducer;
